import { useState, useMemo, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Camera, X, Check, Info } from 'lucide-react';

interface ShotInfo {
  prompt: string;
  description: string;
  imageUrl: string;
}

interface ShotDataWithInfo {
  [category: string]: {
    [name: string]: ShotInfo;
  };
}

// Enhanced shot data with descriptions and example images
const shotData: ShotDataWithInfo = {
  'Shots': {
    'Extreme wide shot': {
      prompt: 'extreme wide shot, subject tiny in frame, 16mm, vast environment, aerial feel',
      description: 'Shows the subject as a tiny element within a vast landscape. Perfect for establishing scale and environment.',
      imageUrl: 'https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?w=300&h=200&fit=crop'
    },
    'Aerial / Drone shot': {
      prompt: 'aerial shot, bird\'s-eye perspective, wide view, cinematic scale',
      description: 'Bird\'s-eye view from above, creating a sense of grandeur and cinematic scale.',
      imageUrl: 'https://images.unsplash.com/photo-1508739773434-c26b3d09e071?w=300&h=200&fit=crop'
    },
    'Wide shot / Full shot': {
      prompt: 'wide shot, full body, environmental portrait, 24mm, rule of thirds',
      description: 'Shows the full subject with surrounding environment. Great for context and storytelling.',
      imageUrl: 'https://images.unsplash.com/photo-1517841905240-472988babdf9?w=300&h=200&fit=crop'
    },
    'Long shot (LS)': {
      prompt: 'long shot, subject small but readable, 35mm, cinematic distance',
      description: 'Subject is visible but small, emphasizing the environment and creating cinematic distance.',
      imageUrl: 'https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?w=300&h=200&fit=crop'
    },
    'Full body': {
      prompt: 'full body shot, head-to-toe, 35mm, centered framing',
      description: 'Complete head-to-toe view of the subject with balanced, centered composition.',
      imageUrl: 'https://images.unsplash.com/photo-1469334031218-e382a71b716b?w=300&h=200&fit=crop'
    },
    'Three-quarter (3/4)': {
      prompt: 'three-quarter shot, knees-up, 50mm, dynamic pose',
      description: 'From knees up, creating a dynamic and engaging perspective often used in fashion.',
      imageUrl: 'https://images.unsplash.com/photo-1509631179647-0177331693ae?w=300&h=200&fit=crop'
    },
    'Medium shot': {
      prompt: 'medium shot, waist-up, 50mm, conversational framing',
      description: 'Waist-up framing that feels natural and conversational, like talking to someone.',
      imageUrl: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=300&h=200&fit=crop'
    },
    'Medium close-up': {
      prompt: 'medium close-up, chest-up, 85mm, intimate',
      description: 'Chest-up framing for an intimate feel while still showing some body language.',
      imageUrl: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=300&h=200&fit=crop'
    },
    'Close-up (CU)': {
      prompt: 'close-up, head and face filling frame, 85mm, shallow DOF',
      description: 'Face fills the frame, capturing emotions and details with shallow depth of field.',
      imageUrl: 'https://images.unsplash.com/photo-1552058544-f2b08422138a?w=300&h=200&fit=crop'
    },
    'Extreme close-up': {
      prompt: 'extreme close-up, eye detail, macro, sharp texture',
      description: 'Ultra-tight framing on specific details like eyes, lips, or textures.',
      imageUrl: 'https://images.unsplash.com/photo-1508186225823-0963cf9ab0de?w=300&h=200&fit=crop'
    },
    'Head-and-shoulders': {
      prompt: 'head-and-shoulders portrait, 85mm, soft background, 4:5 vertical',
      description: 'Classic portrait framing from head to shoulders with soft, blurred background.',
      imageUrl: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=300&h=200&fit=crop'
    },
    'Beauty shot': {
      prompt: 'beauty shot, tight face, flawless skin, 85mm, soft studio light',
      description: 'Tight facial shot emphasizing skin, makeup, and beauty with soft lighting.',
      imageUrl: 'https://images.unsplash.com/photo-1616683693504-3ea7e9ad6fec?w=300&h=200&fit=crop'
    },
    'Insert / Cut-in': {
      prompt: 'insert shot, close detail of prop, crisp texture, shallow DOF',
      description: 'Close detail shot of an object or prop, showing texture and craftsmanship.',
      imageUrl: 'https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=300&h=200&fit=crop'
    },
    'Macro / Extreme detail': {
      prompt: 'macro shot, extreme detail, 1:1 magnification, texture focused',
      description: 'Extreme magnification revealing tiny details invisible to the naked eye.',
      imageUrl: 'https://images.unsplash.com/photo-1550159930-40066082a4fc?w=300&h=200&fit=crop'
    },
  },
  'Angles': {
    'Hero shot': {
      prompt: 'hero shot, low angle, dramatic lighting, subject dominant',
      description: 'Low angle looking up at the subject, making them appear powerful and heroic.',
      imageUrl: 'https://images.unsplash.com/photo-1519058082700-08a0b56da9b4?w=300&h=200&fit=crop'
    },
    'Over-the-shoulder': {
      prompt: 'over-the-shoulder shot, foreground element visible, 50mm, contextual',
      description: 'Camera positioned behind one subject, looking at another. Creates depth and context.',
      imageUrl: 'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=300&h=200&fit=crop'
    },
    'Point-of-View (POV)': {
      prompt: 'POV shot, eye-level, hands in frame, immersive',
      description: 'Shows what the subject sees, often with hands visible. Creates immersion.',
      imageUrl: 'https://images.unsplash.com/photo-1527631746610-bca00a040d60?w=300&h=200&fit=crop'
    },
    'High angle': {
      prompt: 'high angle shot, looking down on subject, 35mm, vulnerable feel',
      description: 'Camera looks down on the subject, making them appear smaller or vulnerable.',
      imageUrl: 'https://images.unsplash.com/photo-1504439904031-93ded9f93e4e?w=300&h=200&fit=crop'
    },
    'Top-down / Flat lay': {
      prompt: 'top-down shot, directly overhead, flat lay composition, meticulous arrangement',
      description: 'Directly overhead view, perfect for product arrangements and food photography.',
      imageUrl: 'https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?w=300&h=200&fit=crop'
    },
    'Low angle': {
      prompt: 'low angle shot, camera near ground, 24–35mm, monumental look',
      description: 'Camera near ground looking up, making subjects appear grand and imposing.',
      imageUrl: 'https://images.unsplash.com/photo-1449157291145-7efd050a4d0e?w=300&h=200&fit=crop'
    },
    'Dutch angle': {
      prompt: 'dutch angle, tilted frame, unsettling vibe, slight rotation',
      description: 'Tilted camera creating unease or dynamic energy. Used for dramatic effect.',
      imageUrl: 'https://images.unsplash.com/photo-1470723710355-95304d8aece4?w=300&h=200&fit=crop'
    },
  },
  'Style': {
    'Silhouette': {
      prompt: 'silhouette, strong backlight, subject dark outline, high contrast',
      description: 'Subject appears as a dark shape against bright background. Mysterious and dramatic.',
      imageUrl: 'https://images.unsplash.com/photo-1516912481808-3406841bd33c?w=300&h=200&fit=crop'
    },
    'Reflection': {
      prompt: 'reflection shot, mirror/water reflection, symmetrical composition',
      description: 'Uses reflective surfaces to create symmetry and visual interest.',
      imageUrl: 'https://images.unsplash.com/photo-1597097458412-7dbb35fa1fcf?w=300&h=200&fit=crop'
    },
    'Shadow play': {
      prompt: 'shadow play, creative shadows, light patterns, artistic mood',
      description: 'Creative use of shadows and light patterns to add depth and mood.',
      imageUrl: 'https://images.unsplash.com/photo-1499209974431-9dddcece7f88?w=300&h=200&fit=crop'
    },
    'Freeze action': {
      prompt: 'freeze action, 1/1000s shutter, sharp motion stop',
      description: 'Captures fast movement frozen in time with crystal clarity.',
      imageUrl: 'https://images.unsplash.com/photo-1530549387789-4c1017266635?w=300&h=200&fit=crop'
    },
    'Environmental portrait': {
      prompt: 'environmental portrait, subject integrated in scene, 35mm, contextual storytelling',
      description: 'Portrait where the environment tells part of the subject\'s story.',
      imageUrl: 'https://images.unsplash.com/photo-1559582798-678dfc71ccd8?w=300&h=200&fit=crop'
    },
    'Frame-within-frame': {
      prompt: 'frame within frame, subject framed by window/doorway, layered depth',
      description: 'Subject framed by architectural elements, adding layers and focus.',
      imageUrl: 'https://images.unsplash.com/photo-1494548162494-384bba4ab999?w=300&h=200&fit=crop'
    },
    'Two-shot': {
      prompt: 'two-shot, both subjects in frame, balanced composition, 35–50mm',
      description: 'Two subjects in frame with balanced composition. Great for relationships.',
      imageUrl: 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=300&h=200&fit=crop'
    },
    'Group / Crowd shot': {
      prompt: 'group shot, even lighting, wide lens, organized composition',
      description: 'Multiple people arranged with even lighting and organized composition.',
      imageUrl: 'https://images.unsplash.com/photo-1529156069898-49953e39b3ac?w=300&h=200&fit=crop'
    }
  }
};

// Helper to get prompt from shot info
const getPrompt = (category: string, shotName: string): string => {
  return shotData[category]?.[shotName]?.prompt ?? '';
};

interface ShotSelectorProps {
  selectedPrompts: string[];
  setSelectedPrompts: (prompts: string[]) => void;
}

// Tooltip component with image preview - now uses portal to render outside overflow containers
const ShotTooltip = ({ shot, name, anchorRef, isVisible }: { shot: ShotInfo; name: string; anchorRef: React.RefObject<HTMLDivElement>; isVisible: boolean }) => {
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
  
  useEffect(() => {
    if (isVisible && anchorRef.current) {
      const rect = anchorRef.current.getBoundingClientRect();
      const tooltipWidth = 288; // w-72 = 18rem = 288px
      const tooltipHeight = 200; // approximate height
      
      let left = rect.left + rect.width / 2 - tooltipWidth / 2;
      let top = rect.top - tooltipHeight - 8;
      
      // Keep tooltip within viewport horizontally
      if (left < 8) left = 8;
      if (left + tooltipWidth > window.innerWidth - 8) {
        left = window.innerWidth - tooltipWidth - 8;
      }
      
      // If not enough space above, show below
      if (top < 8) {
        top = rect.bottom + 8;
      }
      
      setPosition({ top, left });
    } else {
      // Reset position when not visible so it recalculates on next show
      setPosition(null);
    }
  }, [isVisible, anchorRef]);

  // Don't render until we have a valid position
  if (!isVisible || !position) return null;

  return createPortal(
    <div 
      className="fixed w-72 p-0 bg-gray-900 border border-gray-700 rounded-xl shadow-2xl z-[9999] overflow-hidden animate-fade-in"
      style={{ top: position.top, left: position.left }}
    >
      {/* Image */}
      <div className="relative h-36 overflow-hidden">
        <img 
          src={shot.imageUrl} 
          alt={name}
          className="w-full h-full object-cover"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-transparent to-transparent" />
        <div className="absolute bottom-2 left-3 right-3">
          <p className="text-white font-semibold text-sm drop-shadow-lg">{name}</p>
        </div>
      </div>
      {/* Description */}
      <div className="p-3">
        <p className="text-gray-300 text-xs leading-relaxed">{shot.description}</p>
      </div>
    </div>,
    document.body
  );
};

// Shot card component to handle individual hover state
const ShotCard = ({ 
  shotName, 
  shotInfo, 
  isSelected, 
  onToggle, 
  category 
}: { 
  shotName: string; 
  shotInfo: ShotInfo; 
  isSelected: boolean; 
  onToggle: (category: string, shotName: string) => void;
  category: string;
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  return (
    <div 
      ref={cardRef}
      className="relative"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <button
        onClick={() => onToggle(category, shotName)}
        className={`
          w-full relative overflow-hidden rounded-xl border-2 transition-all duration-200
          ${isSelected 
            ? 'border-blue-500 bg-blue-500/10 shadow-lg shadow-blue-500/20' 
            : 'border-gray-700 bg-gray-800/40 hover:border-gray-600 hover:bg-gray-800/60'
          }
        `}
      >
        {/* Mini preview image */}
        <div className="relative h-16 overflow-hidden">
          <img 
            src={shotInfo.imageUrl} 
            alt={shotName}
            className={`w-full h-full object-cover transition-all duration-200 ${isSelected ? 'brightness-110' : 'brightness-75 hover:brightness-90'}`}
            loading="lazy"
          />
          <div className={`absolute inset-0 transition-all duration-200 ${isSelected ? 'bg-blue-500/20' : 'bg-gradient-to-t from-gray-900/80 to-transparent'}`} />
          
          {/* Selection indicator */}
          {isSelected && (
            <div className="absolute top-1.5 right-1.5 w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center shadow-lg">
              <Check className="w-3 h-3 text-white" />
            </div>
          )}
          
          {/* Info icon for tooltip trigger */}
          <div className={`absolute top-1.5 left-1.5 w-5 h-5 bg-black/50 rounded-full flex items-center justify-center transition-opacity ${isHovered ? 'opacity-100' : 'opacity-0'}`}>
            <Info className="w-3 h-3 text-white/80" />
          </div>
        </div>
        
        {/* Shot name */}
        <div className="px-2 py-2">
          <p className={`text-xs font-medium text-left truncate transition-colors ${isSelected ? 'text-blue-300' : 'text-gray-300'}`}>
            {shotName}
          </p>
        </div>
      </button>
      
      {/* Tooltip - renders via portal */}
      <ShotTooltip shot={shotInfo} name={shotName} anchorRef={cardRef} isVisible={isHovered} />
    </div>
  );
};

export default function ShotSelector({ selectedPrompts, setSelectedPrompts }: ShotSelectorProps) {
  const [activeCategory, setActiveCategory] = useState<string>('Shots');
  const [isExpanded, setIsExpanded] = useState(false);

  // Derive selectedShots from selectedPrompts (parent state)
  const selectedShots = useMemo(() => {
    const map = new Map<string, Set<string>>();
    for (const prompt of selectedPrompts) {
      for (const category in shotData) {
        for (const shotName in shotData[category]) {
          if (shotData[category][shotName].prompt === prompt) {
            if (!map.has(category)) {
              map.set(category, new Set());
            }
            map.get(category)!.add(shotName);
            break;
          }
        }
      }
    }
    return map;
  }, [selectedPrompts]);

  const toggleShot = (category: string, shotName: string) => {
    const prompt = getPrompt(category, shotName);
    if (!prompt) return;

    const newPrompts = selectedPrompts.includes(prompt)
      ? selectedPrompts.filter(p => p !== prompt)
      : [...selectedPrompts, prompt];
    
    setSelectedPrompts(newPrompts);
  };

  const removeShot = (prompt: string) => {
    setSelectedPrompts(selectedPrompts.filter(p => p !== prompt));
  };

  const getTotalSelected = (): number => {
    return selectedPrompts.length;
  };

  const getCategorySelectedCount = (category: string): number => {
    return selectedShots.get(category)?.size ?? 0;
  };

  // Find shot name from prompt for display
  const getShotNameFromPrompt = (prompt: string): { category: string; name: string } | null => {
    for (const category in shotData) {
      for (const shotName in shotData[category]) {
        if (shotData[category][shotName].prompt === prompt) {
          return { category, name: shotName };
        }
      }
    }
    return null;
  };

  const categories = Object.keys(shotData);

  const expandedDiv = (
    <div className="space-y-4">
      {/* Selected shots as removable chips */}
      {selectedPrompts.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-gray-400 uppercase tracking-wide">Selected ({selectedPrompts.length})</span>
            <button
              onClick={() => setSelectedPrompts([])}
              className="text-xs text-red-400 hover:text-red-300 transition-colors"
            >
              Clear all
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {selectedPrompts.map((prompt) => {
              const shotInfo = getShotNameFromPrompt(prompt);
              if (!shotInfo) return null;
              return (
                <button
                  key={prompt}
                  onClick={() => removeShot(prompt)}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-blue-500/20 border border-blue-500/40 text-blue-300 rounded-lg text-xs font-medium hover:bg-blue-500/30 hover:border-blue-500/50 transition-all group"
                >
                  <span className="max-w-[120px] truncate">{shotInfo.name}</span>
                  <X className="w-3 h-3 opacity-60 group-hover:opacity-100 transition-opacity" />
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Category tabs */}
      <div className="flex gap-1 p-1 bg-gray-900/50 rounded-lg">
        {categories.map((category) => {
          const count = getCategorySelectedCount(category);
          const isActive = activeCategory === category;
          return (
            <button
              key={category}
              onClick={() => setActiveCategory(category)}
              className={`
                flex-1 relative px-3 py-2 rounded-md text-sm font-medium transition-all duration-200
                ${isActive 
                  ? 'bg-gray-700 text-white shadow-sm' 
                  : 'text-gray-400 hover:text-gray-300 hover:bg-gray-800/50'
                }
              `}
            >
              {category}
              {count > 0 && (
                <span className={`
                  absolute -top-1.5 -right-1.5 min-w-5 h-5 flex items-center justify-center
                  text-[10px] font-bold rounded-full
                  ${isActive ? 'bg-blue-500 text-white' : 'bg-blue-500/80 text-white'}
                `}>
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Shot grid for active category - using ShotCard component */}
      <div className="grid grid-cols-2 gap-2">
        {Object.entries(shotData[activeCategory] || {}).map(([shotName, shotInfo]) => {
          const isSelected = selectedShots.get(activeCategory)?.has(shotName) ?? false;
          return (
            <ShotCard
              key={shotName}
              shotName={shotName}
              shotInfo={shotInfo}
              isSelected={isSelected}
              onToggle={toggleShot}
              category={activeCategory}
            />
          );
        })}
      </div>
    </div>
  );

  return (
    <div className="bg-gray-800/40 backdrop-blur-xl border border-gray-700/50 rounded-xl overflow-hidden shadow-lg">
      <button
        onClick={() => setIsExpanded(state => !state)}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-700/20 transition"
      >
        <div className="flex items-center gap-2">
          <Camera className="w-4 h-4 text-green-400" />
          <h3 className="text-sm font-semibold text-gray-200">Shot Presets</h3>
          {getTotalSelected() > 0 && (
            <div className="flex items-center gap-1.5 ml-1">
              <div className="flex -space-x-1">
                {selectedPrompts.slice(0, 3).map((prompt, i) => {
                  const shotInfo = getShotNameFromPrompt(prompt);
                  if (!shotInfo) return null;
                  const shot = shotData[shotInfo.category]?.[shotInfo.name];
                  return (
                    <div 
                      key={i}
                      className="w-5 h-5 rounded-full border-2 border-gray-800 overflow-hidden"
                      style={{ zIndex: 3 - i }}
                    >
                      <img src={shot?.imageUrl} alt="" className="w-full h-full object-cover" />
                    </div>
                  );
                })}
              </div>
              {selectedPrompts.length > 3 && (
                <span className="text-xs text-gray-400">+{selectedPrompts.length - 3}</span>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          {getTotalSelected() > 0 && (
            <span className="px-2 py-0.5 text-xs font-bold bg-green-500/20 text-green-400 rounded-full">
              {getTotalSelected()}
            </span>
          )}
          <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
        </div>
      </button>
      
      {isExpanded && (
        <div className="px-4 pb-4 pt-2 max-h-[500px] overflow-y-auto">
          {expandedDiv}
        </div>
      )}
    </div>
  );
}
